import React, { useState, createContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    All_TIME_PERIOD,
    CUSTOM_PERIOD,
    SEARCH_FILTERS_INVOICES,
    INVOICE_STATUS,
    REAL_INVOICE_SUBTYPES,
    COMMERCIAL_DOCUMENT_SUBTYPES,
} from 'Utils/constants';
import { formatDateISO } from 'Utils/formatters';

const InvoicesFiltersContext = createContext();

export const INVOICES_TABS = {
    ALL: 'all',
    COLLECTIONS: 'collections',
    PAYMENTS: 'payments',
    COMMERCIAL_DOCUMENTS: 'commercial-documents',
    DRAFTS: 'drafts',
    INVOICES_WITH_ERRORS: 'invoices-with-errors',
};

const isCommercialDocumentsTab = (tab) => tab === INVOICES_TABS.COMMERCIAL_DOCUMENTS;

// Acota los subtipos enviados al backend según el tab activo: el tab de documentos
// comerciales solo opera con presupuestos/proformas/albaranes y el resto de tabs solo
// con facturas reales. Si el usuario no ha seleccionado subtipos, se envía el grupo
// completo del tab para que las facturas reales y los documentos comerciales nunca se mezclen.
const getScopedSubtypes = (tab, subtypes) => {
    const scope = isCommercialDocumentsTab(tab) ? COMMERCIAL_DOCUMENT_SUBTYPES : REAL_INVOICE_SUBTYPES;
    const selectedInScope = subtypes.filter((subtype) => scope.includes(subtype));
    return selectedInScope.length === 0 ? scope : selectedInScope;
};

const isEqual = (a, b) => {
    if (typeof a !== typeof b) return false;

    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) {
            return false;
        }
        return a.every((val, index) => val === b[index]);
    }

    return a === b;
};

export function useInvoicesFilters() {
    const context = React.useContext(InvoicesFiltersContext);

    if (context === undefined) {
        throw new Error(`useInvoicesFilters must be used within a InvoicesFiltersProvider`);
    }
    return context;
}

const NULLISH_DATE_PERIOD = ['all_time', 'custom'];

export const DATE_FIELD_OPTIONS = {
    DATE_EMISSION: 'date_emission',
    DATE_OPERATION: 'date_operation',
    DATE_EXPIRATION: 'date_expiration',
    DATE_PAYMENT: 'date_payment',
};

export const INITIAL_FILTERS = {
    query: '',
    date_period: All_TIME_PERIOD,
    date_from: '',
    date_to: '',
    date_type: DATE_FIELD_OPTIONS.DATE_EMISSION,
    amount_from: '',
    amount_to: '',
    type: null,
    subtypes: [],
    contacts_ids: null,
    accounts_products_ids: null,
    integrations_ids: null,
    integrations_include_empty: false,
    accounting_delivered_mode: SEARCH_FILTERS_INVOICES.ACCOUNTING_DELIVERED_MODE.ALL,
    status: INVOICE_STATUS.OK,
    reconciled_statuses: [],
    issued_mode: SEARCH_FILTERS_INVOICES.ISSUED_MODE.ALL,
    imported_mode: SEARCH_FILTERS_INVOICES.IMPORTED_MODE.ALL,
    cancelled_mode: SEARCH_FILTERS_INVOICES.CANCELLED_MODE.ALL,
    contacts_include_empty: null,
};

const getExportParams = ({ filters, order, orderBy, tab }) => {
    return {
        query: filters.query === '' ? null : filters.query,
        type: filters.type,
        subtypes: getScopedSubtypes(tab, filters.subtypes),
        reconciled_statuses: filters.reconciled_statuses.length === 0 ? null : filters.reconciled_statuses,
        amount_from: filters.amount_from === '' ? null : filters.amount_from,
        amount_to: filters.amount_to === '' ? null : filters.amount_to,
        date_from: filters.date_from ? formatDateISO(filters.date_from) : null,
        date_to: filters.date_to ? formatDateISO(filters.date_to) : null,
        date_period: NULLISH_DATE_PERIOD.includes(filters.date_period) ? null : filters.date_period,
        date_type: filters.date_type,
        contacts_ids: filters.contacts_ids,
        accounts_products_ids: filters.accounts_products_ids,
        integrations_ids: filters.integrations_ids,
        integrations_include_empty: filters.integrations_include_empty ? true : null,
        accounting_delivered_mode: filters.accounting_delivered_mode,
        status: filters.status,
        issued_mode: filters.issued_mode,
        imported_mode: filters.imported_mode,
        cancelled_mode: filters.cancelled_mode,
        contacts_include_empty: filters.contacts_include_empty,
        invoices_ids: null,
        taxonomies_tags_ids: null,
        accounts_products_include_empty: null,
        order: order,
        order_by: orderBy,
    };
};

const filtersKeys = [
    'subtypes',
    'reconciled_statuses',
    'contacts_ids',
    'accounts_products_ids',
    'amount_from',
    'amount_to',
    'integrations_ids',
    'integrations_include_empty',
    'date_period',
    'accounting_delivered_mode',
    'issued_mode',
    'imported_mode',
    'cancelled_mode',
    'contacts_include_empty',
];

const groupedFiltersKeys = [['contacts_ids', 'contacts_include_empty']];

export default function InvoicesFiltersProvider({ children }) {
    const [filters, _setFilters] = useState(INITIAL_FILTERS);
    const [tab, setTab] = useState(INVOICES_TABS.ALL);
    const [orderBy, setOrderBy] = useState('date_emission');
    const [order, setOrder] = useState('desc');

    const setFilter = (key, value) => {
        _setFilters((prevState) => ({
            ...prevState,
            [key]: value,
        }));
    };

    const navigateToTab = (newTab) => {
        // Los subtipos de facturas reales y de documentos comerciales son excluyentes,
        // así que al cruzar entre ambos grupos se resetea la selección del filtro.
        if (isCommercialDocumentsTab(newTab) !== isCommercialDocumentsTab(tab)) {
            setFilter('subtypes', []);
        }

        switch (newTab) {
            case INVOICES_TABS.ALL:
                setFilter('status', INVOICE_STATUS.OK);
                setFilter('type', null);
                break;
            case INVOICES_TABS.COLLECTIONS:
                setFilter('status', INVOICE_STATUS.OK);
                setFilter('type', 'sale');
                break;
            case INVOICES_TABS.PAYMENTS:
                setFilter('status', INVOICE_STATUS.OK);
                setFilter('type', 'purchase');
                break;
            case INVOICES_TABS.COMMERCIAL_DOCUMENTS:
                setFilter('status', INVOICE_STATUS.OK);
                setFilter('type', null);
                break;
            case INVOICES_TABS.DRAFTS:
                setFilter('status', INVOICE_STATUS.DRAFTED);
                setFilter('type', null);
                break;
            case INVOICES_TABS.INVOICES_WITH_ERRORS:
                setFilter('status', INVOICE_STATUS.WITH_ERRORS);
                setFilter('type', null);
                break;
            default:
                break;
        }

        setTab(newTab);
    };

    const filtersAppliedCount = useMemo(() => {
        const modifiedFilters = filtersKeys.filter((key) => !isEqual(filters[key], INITIAL_FILTERS[key]));

        let count = modifiedFilters.length;

        groupedFiltersKeys.forEach((group) => {
            const filteredGroup = group.filter((key) => modifiedFilters.includes(key));
            if (filteredGroup.length > 1) {
                count -= filteredGroup.length - 1;
            }
        });

        return Math.max(count, 0);
    }, [filters]);

    const clearFilters = useCallback(() => {
        const defaultFilters = filtersKeys.reduce((acc, key) => {
            acc[key] = INITIAL_FILTERS[key];
            return acc;
        }, {});

        _setFilters((prevState) => ({
            ...prevState,
            ...defaultFilters,
        }));
    }, []);

    const value = useMemo(
        () => ({
            order,
            setOrder,
            orderBy,
            setOrderBy,
            filters,
            setFilter,
            filtersAppliedCount,
            clearFilters,
            params: getExportParams({ filters, order, orderBy, tab }),
            tab,
            navigateToTab,
            searchFilters: {
                issued_mode: filters.issued_mode,
                imported_mode: filters.imported_mode,
                cancelled_mode: filters.cancelled_mode,
                status: filters.status,
                type: filters.type,
                subtypes: getScopedSubtypes(tab, filters.subtypes),
                query: filters.query ? filters.query : null,
                reconciled_statuses: filters.reconciled_statuses.length === 0 ? null : filters.reconciled_statuses,
                date_period: filters.date_period ? filters.date_period : null,
                date_type: filters.date_type,
                amount_from: filters.amount_from === '' ? null : filters.amount_from,
                amount_to: filters.amount_to === '' ? null : filters.amount_to,
                integrations_ids: filters.integrations_ids,
                integrations_include_empty: filters.integrations_include_empty ? true : null,
                contacts_ids: filters.contacts_ids,
                accounting_delivered_mode: filters.accounting_delivered_mode,
                contacts_include_empty: filters.contacts_include_empty,
                order: order,
                order_by: orderBy,
                ...(filters.accounts_products_ids && {
                    accounts_products_ids: filters.accounts_products_ids,
                }),
                ...(filters.date_period === CUSTOM_PERIOD && {
                    date_from: filters.date_from ? formatDateISO(filters.date_from) : null,
                    date_to: filters.date_to ? formatDateISO(filters.date_to) : null,
                }),
            },
        }),
        [filters, filtersAppliedCount, clearFilters, tab, order, orderBy],
    );

    return <InvoicesFiltersContext.Provider value={value}>{children}</InvoicesFiltersContext.Provider>;
}

InvoicesFiltersProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
